import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/Auth";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { ArrowLeft, Package, Calendar, Hash } from "lucide-react";

const NewDrugBatch = () => {
  const [searchParams] = useSearchParams();
  const branch = searchParams.get("branch");
  const drug = searchParams.get("drug");

  const { requestWithAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [drugDetails, setDrugDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const [batchData, setBatchData] = useState({
    branch: branch,
    drug: drug,
    expiry_date: "",
    quantity: "",
  });

  const handleChange = (e) => {
    setBatchData({ ...batchData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!batchData.quantity) {
      newErrors.quantity = "Quantity is required";
    } else if (batchData.quantity <= 0) {
      newErrors.quantity = "Quantity must be greater than 0";
    }
    if (!batchData.expiry_date || batchData.expiry_date === "") {
      newErrors.expiry_date = "Expiry date is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchDrugDetails = async () => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/branch/${branch}/${drug}/item/`,
        "GET"
      );
      setDrugDetails(data);
    } catch (error) {
      toast.error("Failed to get inventory detail");
    } finally {
      setLoading(false);
    }
  };

  const createBatchTrack = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setUpdating(true);
    try {
      await requestWithAuth(
        `/inventory/branch/batches/list`,
        "POST",
        JSON.stringify(batchData),
        {
          "Content-Type": "application/json"
        }
      );
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Batch created successfully",
      }).then(() => {
        window.history.back();
      });
    } catch (error) {
      if (error.response?.data?.errors) {
        for (const property in error.response.data.errors) {
          toast.error(`${error.response.data.errors[property]}`);
        }
      } else {
        toast.error("Failed to create batch");
      }
    } finally {
      setUpdating(false);
    }
  };

  const renderField = (label, name, type = "text", required = false, step = null, disabled = false, value = "") => (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        name={name}
        id={name}
        step={step}
        value={value}
        disabled={disabled}
        placeholder={disabled ? "" : `Enter ${label.toLowerCase()}`}
        onChange={handleChange}
        className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all ${disabled
            ? "bg-gray-50 text-gray-500 cursor-not-allowed"
            : errors[name]
              ? "border-red-300 focus:ring-red-500 focus:border-red-500"
              : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
          }`}
      />
      {errors[name] && (
        <p className="mt-1.5 text-xs text-red-600">{errors[name]}</p>
      )}
    </div>
  );

  useEffect(() => {
    fetchDrugDetails();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-600">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="text-lg">Loading drug details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header with Back Button */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors group"
          >
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back to Batch Tracking</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span>Batch Tracking</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Create</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Create New Batch</h1>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Card Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Package className="text-blue-500" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Batch Information</h2>
                <p className="text-sm text-gray-500">Enter the details for the new batch</p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={createBatchTrack} className="p-6 space-y-6">
            {/* Drug Details Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200 flex items-center gap-2">
                <Package size={16} />
                Drug Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField(
                  "Drug Name",
                  "drug_name",
                  "text",
                  false,
                  null,
                  true,
                  drugDetails?.drug?.name || ""
                )}
                {renderField(
                  "Branch Name",
                  "branch_name",
                  "text",
                  false,
                  null,
                  true,
                  drugDetails?.branch?.name || ""
                )}
              </div>
            </div>

            {/* Batch Information Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200 flex items-center gap-2">
                <Hash size={16} />
                Batch Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField("Quantity", "quantity", "number", true, "0.01", false, batchData?.quantity || "")}
                <div>
                  <label htmlFor="expiry_date" className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="expiry_date"
                      id="expiry_date"
                      onChange={handleChange}
                      className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all ${errors.expiry_date
                          ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
                        }`}
                    />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                  </div>
                  {errors.expiry_date && (
                    <p className="mt-1.5 text-xs text-red-600">{errors.expiry_date}</p>
                  )}
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Quantity can be entered with up to 2 decimal places
              </p>
            </div>

            {/* Form Footer */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={updating}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>

                {!updating ? (
                  <button
                    type="submit"
                    className="px-6 py-2.5 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
                  >
                    Create Batch
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="px-6 py-2.5 text-sm font-medium text-white bg-blue-400 rounded-lg cursor-not-allowed flex items-center gap-2"
                  >
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Help Text */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-900">Batch Tracking</h3>
              <p className="mt-1 text-sm text-blue-700">
                Track individual batches to monitor expiry dates and manage stock levels effectively. This helps prevent expired medications from being dispensed.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewDrugBatch;